<!-- We simply log the caught error to the console inside the svelte code block, so we can suppress the warning. -->
<!--suppress JSVoidFunctionReturnValueUsed -->
<script lang="ts">
	import Skeleton from 'svelte-skeleton/Skeleton.svelte'
	import { fetchDashCardData } from './DashCard.controller'
	import { getContext } from 'svelte'
	import MediaQuery from '../../templates/MediaQuery.svelte'
	import type { DashCardParams } from '../DashboardPage.model'
	import { t } from '$i18n/i18n'
	import SecondaryButton, { SecondaryBtnColors } from '../../atoms/SecondaryButton.svelte'
	import { ButtonIcons } from '$types/enums'
	import Icon from '../../atoms/Icon.svelte'
	import iconError from '@/icons/icon_error_crossmark_circle_red.svg'
	import { ComparisonType } from './DashCard.model'
	import type { Writable } from 'svelte/store'
	import { roundToNearest, Unit } from '$utils/formatters'

	const cardQueryData: Writable<DashCardParams> = getContext('cardQueryData')

	let { cardId, cardTitle }: { cardId: string; cardTitle: string } = $props()

	let unique = $state({}) // every {} is unique, {} === {} evaluates to false

	/**
	 * Helper function to generate the comparison result string.
	 *
	 * @param compResult The comparison result.
	 * @param compType The comparison type.
	 * @param unit The unit of the main result.
	 */
	function generateCompResultString(
		compResult: number,
		compType: ComparisonType,
		unit: string
	): string {
		if (
			compType === ComparisonType.NO_COMPARISON ||
			compResult === undefined ||
			compResult === null
		) {
			return ''
		}
		let result = roundToNearest(
			compResult,
			getUnitForCompType(compType),
			getMaximumDecimalsForCompType(compType),
			false
		)
		// Add + sign for CHANGE comparison types when result is positive
		if (
			(compType === ComparisonType.PERCENTAGE_CHANGE_COMPARISON ||
				compType === ComparisonType.ABSOLUTE_CHANGE_COMPARISON) &&
			compResult > 0
		) {
			result = `+${result}`
		}

		// Add appropriate suffix based on comparison type
		switch (compType) {
			case ComparisonType.PERCENTAGE_COMPARISON:
			case ComparisonType.PERCENTAGE_CHANGE_COMPARISON:
				return `${result} %`
			case ComparisonType.ABSOLUTE_COMPARISON:
			case ComparisonType.ABSOLUTE_CHANGE_COMPARISON:
				return `${result} ${unit}`
			default:
				return result
		}
	}

	function getMaximumDecimalsForCompType(compType: ComparisonType): number {
		switch (compType) {
			case ComparisonType.PERCENTAGE_COMPARISON:
			case ComparisonType.PERCENTAGE_CHANGE_COMPARISON:
				return 1
			default:
				return 2
		}
	}

	function getUnitForCompType(compType: ComparisonType): Unit {
		switch (compType) {
			case ComparisonType.PERCENTAGE_COMPARISON:
			case ComparisonType.PERCENTAGE_CHANGE_COMPARISON:
				return Unit.PERCENT
			default:
				return Unit.NONE
		}
	}

	/**
	 * Set the arrow direction based on compResult iff the comparison type is a change comparison, otherwise show info
	 * icon.
	 * The default arrow direction is up.
	 *
	 * @param compResult The comparison result.
	 * @param compType The comparison type.
	 */
	function getIconClass(compType: ComparisonType, compResult: number): string {
		if (
			compType === ComparisonType.NO_COMPARISON ||
			compType === ComparisonType.PERCENTAGE_COMPARISON ||
			compType === ComparisonType.ABSOLUTE_COMPARISON ||
			compResult === undefined ||
			compResult === null
		) {
			return 'dashCard__info_icon'
		}
		let result = 'dashCard__arrow-blue'
		if (compResult === 0) {
			result += ' dashCard__arrow-neutral'
		} else if (compResult < 0) {
			result += ' dashCard__arrow-down'
		}
		return result
	}

	/**
	 * Return a string key for comparison text based on the comparison type.
	 * @param compType The comparison type.
	 * @param compResult The comparison result, to check, whether the value is null or undefined.
	 */
	function getComparisonText(compType: ComparisonType, compResult: number): string {
		if (
			compType === ComparisonType.NO_COMPARISON ||
			compResult === undefined ||
			compResult === null
		) {
			return 'UI.Dashboard.card.noComparison'
		}
		if (
			compType === ComparisonType.PERCENTAGE_COMPARISON ||
			compType === ComparisonType.ABSOLUTE_COMPARISON
		) {
			return 'UI.Dashboard.card.inCompTimeframe'
		}
		return 'UI.Dashboard.card.compTimeframe'
	}

	function reload() {
		unique = {} // Every {} is unique, i.e. this will trigger a reload using #key.
	}
</script>

<div class="dashCard" id="card-{cardId}">
	{#key unique}
		{#await fetchDashCardData(cardId, $cardQueryData)}
			<!-- Loading skeleton -->
			<div class="dashCard__result-container">
				<div class="dashCard__stroke"></div>
				<div class="dashCard__result">
					<h1>{cardTitle}</h1>
					<div class="dashCard__result-text">
						<Skeleton height="1.5rem" width="5rem">
							<rect height="1.5rem" width="5rem" rx="0.25rem" ry="0.25rem" />
						</Skeleton>
					</div>
				</div>
			</div>
			<div class="dashCard__comparison">
				<Skeleton height="2.25rem" width="2.25rem">
					<rect height="2.25rem" width="2.25rem" rx="0.25rem" ry="0.25rem" />
				</Skeleton>
				<div class="dashCard__comparison-data">
					<div class="dashCard__comparison-percent">
						<Skeleton height="1.125rem" width="5.375rem">
							<rect height="1.125rem" width="5.375rem" rx="0.25rem" ry="0.25rem" />
						</Skeleton>
					</div>
					<div class="dashCard__comparison-text">
						<Skeleton height="1.125rem" width="10rem">
							<rect height="1.125rem" width="10rem" rx="0.25rem" ry="0.25rem" />
						</Skeleton>
					</div>
				</div>
			</div>
		{:then dashCardData}
			<!-- Card with data -->
			<MediaQuery query="screen and (min-width: 48rem)" let:matches>
				{#if matches}
					<div class="dashCard__result-container">
						<div class="dashCard__stroke"></div>
						<div class="dashCard__result">
							<h1 title={dashCardData.title}>{dashCardData.title}</h1>
							<span class="dashCard__result-text"
								>{roundToNearest(
									dashCardData.mainResult,
									getUnitForCompType(dashCardData.comparisonType),
									getMaximumDecimalsForCompType(dashCardData.comparisonType),
									false
								)}
								&nbsp;{dashCardData.unit}</span
							>
						</div>
					</div>
					<div class="dashCard__comparison">
						<div class={getIconClass(dashCardData.comparisonType, dashCardData.compResult)}></div>
						<div class="dashCard__comparison-data">
							<span class="dashCard__comparison-percent">
								{generateCompResultString(
									dashCardData.compResult,
									dashCardData.comparisonType,
									dashCardData.unit
								)}
							</span>
							<span class="dashCard__comparison-text"
								>{$t(getComparisonText(dashCardData.comparisonType, dashCardData.compResult))}</span
							>
						</div>
					</div>
				{:else}
					<div class="dashCard__result-container">
						<div class="dashCard__stroke"></div>
						<div class="dashCard__result">
							<h1 title={dashCardData.title}>{dashCardData.title}</h1>
							<div class="dashCard__results_small_container">
								<span class="dashCard__result-text"
									>{dashCardData.mainResult}
									&nbsp;{dashCardData.unit}</span
								>
								<div class="dashCard__comparison">
									<div class="dashCard__comparison-data">
										<div
											class={getIconClass(dashCardData.comparisonType, dashCardData.compResult)}
										></div>
										<span class="dashCard__comparison-percent">
											{generateCompResultString(
												dashCardData.compResult,
												dashCardData.comparisonType,
												dashCardData.unit
											)}
										</span>
									</div>
									<span class="dashCard__comparison-text"
										>{$t(
											getComparisonText(dashCardData.comparisonType, dashCardData.compResult)
										)}</span
									>
								</div>
							</div>
						</div>
					</div>
				{/if}
			</MediaQuery>
		{:catch error}
			<!-- Error card -->
			{@const _ = console.error(`Dashboard card ("${cardTitle}") error:`, JSON.stringify(error))}
			<div class="dashCard__error-container">
				<div class="dashCard__error-main">
					<div class="dashCard__error-headline">
						<Icon src={iconError} size="100%" />
						<h1>{$t('UI.Dashboard.card.serverError')}</h1>
					</div>
					<span class="dashCard__error_desc">{cardTitle} {$t('UI.Dashboard.card.error.desc')}</span>
				</div>
				<div class="dashCard__error_btn_reload">
					<SecondaryButton
						iconSize="24px"
						fullWidth
						color={SecondaryBtnColors.RED}
						leftIcon={ButtonIcons.RELOAD}
						titleAttr={$t('UI.Dashboard.card.error.tryAgain')}
						displayIconOnly
						on:click={reload}
					/>
				</div>
			</div>
		{/await}
	{/key}
</div>

<style lang="scss">
	@use '$styles/global';
	@use '$styles/breakpoints';
	@use '$styles/colors';

	.dashCard {
		container-type: inline-size;
		container-name: card;

		@include global.flex-col(0.875rem, $alignment: flex-start);

		flex: 1;
		flex-basis: auto;
		height: 10.125rem;
		width: 100%;
		padding: 1.5rem;
		border-radius: 0.375rem;
		background: white;
	}

	.dashCard__result-container {
		position: relative;
		width: 100%;
		height: 3.9375rem;
		padding-left: 1.25rem;
	}

	.dashCard__stroke {
		position: absolute;
		left: 0;
		width: 0.25rem;
		height: 100%;
		background: colors.$primaryBlue;
		border-radius: 0.375rem;
	}

	.dashCard__result {
		@include global.flex-col(0.75rem, $alignment: flex-start, $justify: space-between);

		& > h1 {
			line-height: 100%;
			color: var(--color-gray-400); // FIXME when color styleguide is up-to-date
			margin: 0;
			overflow: hidden;
			white-space: nowrap;
			text-overflow: ellipsis;
			width: 100%;
		}
	}

	.dashCard__result-text {
		@include global.roboto-font(100%, 500, 1.5rem);
	}

	.dashCard__comparison {
		@include global.flex-row(0.5rem, $alignment: center);

		height: 2.3125rem;
	}

	.dashCard__arrow-blue {
		background-image: url('@/icons/arrow_blue_up.svg');
		height: 2.25rem;
		width: 2.25rem;
		background-repeat: no-repeat;
	}

	.dashCard__info_icon {
		background-image: url('@/icons/icon_info_blue_outline_20px.svg');
		height: 2rem;
		width: 2rem;
		background-repeat: no-repeat;
	}

	.dashCard__arrow-neutral {
		transform: rotate(90deg);
	}

	.dashCard__arrow-down {
		transform: rotate(180deg);
	}

	.dashCard__comparison-data {
		@include global.flex-row(0.25rem, $alignment: flex-start, $justify: flex-start);

		flex-wrap: wrap;
	}

	.dashCard__comparison-percent {
		@include global.roboto-font(100%, 500, 1.125rem, colors.$primaryBlue);
	}

	.dashCard__comparison-text {
		@include global.roboto-font(
			100%,
			500,
			1.125rem,
			var(--color-gray-400)
		); // FIXME when color styleguide is up-to-date
	}

	.dashCard__error-container {
		@include global.flex-row(0.75rem, $justify: space-between);

		width: 100%;
		height: 100%;
	}

	.dashCard__error-main {
		@include global.flex-col(0.25rem, $alignment: flex-start, $justify: flex-start);

		height: 100%;
	}

	.dashCard__error-headline {
		@include global.flex-row(0.62rem);

		height: 2.25rem;

		& > h1 {
			color: colors.$primaryRed;
			margin: 0;
		}
	}

	.dashCard__error_desc {
		@include global.roboto-font(1.375rem, 500, 1.125rem, var(--color-gray-400));

		display: contents;
	}

	.dashCard__error_btn_reload {
		height: 100%;
		align-content: end;
		width: 2.25rem;
	}

	/** Media Queries **/
	@media screen and (min-width: breakpoints.$small) {
		@container card (width < 10.125rem) {
			.dashCard {
				height: 11.4375rem;
			}

			.dashCard__comparison {
				height: 3.625rem;
			}

			.dashCard__comparison-data {
				flex-direction: column;
			}
		}
	}

	@media screen and (max-width: (breakpoints.$small - 1px)) {
		.dashCard {
			flex-direction: row;
			justify-content: space-between;
			align-items: flex-end;
			height: 7.4375rem;
		}

		.dashCard__result-container {
			width: 100%;
			height: 100%;
		}

		.dashCard__comparison {
			flex-direction: column;
			height: 2.5rem;
			align-items: flex-start;
			row-gap: 0.25rem;
		}

		.dashCard__arrow-blue,
		.dashCard__info_icon {
			height: 1.25rem;
			width: 1.25rem;
		}

		.dashCard__results_small_container {
			@include global.flex-row(0, $alignment: flex-start, $justify: space-between);

			width: 100%;
		}

		.dashCard__comparison-data {
			width: 100%;
			justify-content: flex-end;
			column-gap: 0.25rem;
			flex-direction: row;
			align-items: center;
		}

		.dashCard__comparison-percent {
			font-size: 1rem;
			line-height: normal;
		}

		.dashCard__comparison-text {
			font-size: 0.75rem;
			text-align: end;
		}

		.dashCard__error-headline {
			height: 1.75rem;
		}

		.dashCard__error_desc {
			font-size: 0.875rem;
		}
	}
</style>
